import { usePrevious } from '@sequencehq/utils'
import { useEffect, useMemo } from 'react'

import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { SECONDS } from '@sequencehq/utils/dates'

import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import { CubeStatus } from 'Cube/domain/cube.domain.types'

/**
 * When a quote is published with signature requests enabled, the quote has a 'PREPARING_SIGNATURE_REQUEST'
 * status where we disable the editor. We poll the GET /quotes/:id endpoint to wait until the status changes,
 * then reload Cube so the UI is up to date.
 */
export const usePreparingSignatureRequestPolling = ({
  ready
}: {
  ready: boolean
}) => {
  const cubeContext = useCubeContext()

  const cubeStatus = useMemo(() => {
    return cubeContext.queries.rawData.data.common.status
  }, [cubeContext.queries.rawData.data.common.status])

  const quoteQuery = useQuery(
    dashboard20240730Client.getQuote,
    { id: cubeContext.queries.rawData.data.common.id },
    {
      enabled: cubeStatus === CubeStatus.QuotePreparingSignatureRequest,
      refetchInterval: 1 * SECONDS
    }
  )

  const quoteStatus = quoteQuery.data?.status
  const prevQuoteStatus = usePrevious(quoteQuery.data?.status)

  useEffect(() => {
    if (!ready || quoteStatus === prevQuoteStatus) return

    const reload = async () => {
      await cubeContext.mutators.external.in.core()
    }

    if (
      cubeStatus === CubeStatus.QuotePreparingSignatureRequest &&
      quoteStatus !== 'PREPARING_SIGNATURE_REQUEST'
    ) {
      void reload()
    }
  }, [
    ready,
    cubeContext.mutators.external.in.core,
    cubeStatus,
    quoteStatus,
    prevQuoteStatus
  ])
}
